/**
 * Rota da matriz corporativa (ADR 0101): funções, absorção, competência única e condições. Regras
 * puras, usadas pelo motor e pela simulação, para os dois darem o mesmo resultado.
 */
import { z } from 'zod';
import { toScaled } from './money.ts';
import { currencyCode, decimalString, uuid } from './schemas.ts';

/** Funções da rota, na ordem hierárquica. "role" é um perfil de acesso, usado na validação. */
export const ROUTE_FUNCTIONS = [
  'cost_center_manager',
  'area_manager',
  'head_director',
  'ceo',
  'backoffice_head',
  'role',
] as const;
export type RouteFunction = (typeof ROUTE_FUNCTIONS)[number];

export const STEP_PURPOSES = ['approval', 'validation'] as const;
export type StepPurpose = (typeof STEP_PURPOSES)[number];

export const MATRIX_PROCESS_AREAS = [
  'finance',
  'purchasing',
  'travel',
  'people',
  'it',
  'legal',
  'treasury',
  'assets',
  'facilities',
  'fpa',
] as const;
export type MatrixProcessArea = (typeof MATRIX_PROCESS_AREAS)[number];

export interface MatrixStep {
  fn: RouteFunction;
  purpose?: StepPurpose;
  /** Só em "role": o código do perfil de acesso. */
  roleCode?: string;
}

export interface MatrixBandLike {
  code: string;
  minAmount: string | null;
  maxAmount: string | null;
  steps?: readonly MatrixStep[];
}

/** Tabela que vale para os processos com valor, salvo regra própria. */
export const MATRIX_STANDARD_TABLE = 'FINANCEIRA';

/** Tabelas em que a política dá autonomia: só nelas uma faixa pode não ter quem decida. */
export const MATRIX_AUTONOMY_TABLES: readonly string[] = ['EMERGENCIA'];

/** Faixa sem ninguém que decida aprovaria sem competência, a não ser onde a política manda. */
export const matrixTableIssues = (code: string, bands: readonly MatrixBandLike[]): string[] =>
  MATRIX_AUTONOMY_TABLES.includes(code)
    ? []
    : bands.filter((b) => !b.steps?.length).map((b) => `${b.code} precisa de quem decida`);

/** Tabela sem valor: a única faixa não tem limites e vale para qualquer pedido. */
export const bandHasValue = (b: MatrixBandLike) => b.minAmount != null;

/** Faixa da tabela para o valor, já na moeda dela. Tabela sem valor não olha o valor. */
export function matrixBandFor<B extends MatrixBandLike>(
  bands: readonly B[],
  amount: string | null,
): B | null {
  const withValue = bands.filter(bandHasValue);
  if (withValue.length === 0) return bands[0] ?? null;
  if (amount == null) return null;
  const value = toScaled(amount);
  return (
    withValue.find(
      (b) =>
        value >= toScaled(b.minAmount!) && (b.maxAmount == null || value <= toScaled(b.maxAmount)),
    ) ?? null
  );
}

/** Uma tabela depende de valor quando alguma faixa tem limites. */
export const matrixNeedsAmount = (bands: readonly MatrixBandLike[]) => bands.some(bandHasValue);

/** Faixas de valor em sequência: começam em zero, sem lacuna, e só a última fica sem máximo. */
export function matrixBandIssues(bands: readonly MatrixBandLike[]): string[] {
  const issues: string[] = [];
  const codes = bands.map((b) => b.code);
  for (const code of new Set(codes.filter((c, i) => codes.indexOf(c) !== i))) {
    issues.push(`${code} aparece mais de uma vez`);
  }
  for (const band of bands) {
    const steps = band.steps?.map((s) => `${s.fn}:${s.roleCode ?? ''}`) ?? [];
    if (new Set(steps).size !== steps.length) {
      issues.push(`${band.code} repete uma função na sequência`);
    }
  }
  const valued = bands.filter(bandHasValue);
  if (valued.length === 0) return issues;
  if (valued.length !== bands.length) return ['Misture faixas de valor com condições não'];
  for (const band of valued) {
    if (band.maxAmount != null && toScaled(band.maxAmount) < toScaled(band.minAmount!)) {
      issues.push(`${band.code} termina antes de começar`);
    }
  }
  if (toScaled(valued[0]!.minAmount!) !== 0n)
    issues.push(`${valued[0]!.code} precisa começar em 0`);
  const cent = toScaled('0.01');
  for (const [i, band] of valued.entries()) {
    const next = valued[i + 1];
    if (!next) break;
    if (band.maxAmount == null) {
      issues.push(`${band.code} precisa de valor máximo`);
      continue;
    }
    if (toScaled(next.minAmount!) !== toScaled(band.maxAmount) + cent) {
      issues.push(`${next.code} precisa começar um centavo depois do fim de ${band.code}`);
    }
  }
  return issues;
}

const ORDER = new Map<RouteFunction, number>([
  ['cost_center_manager', 1],
  ['area_manager', 2],
  ['head_director', 3],
  ['ceo', 4],
  ['backoffice_head', 5],
]);

/**
 * Soma as funções das condições à sequência da faixa: cada função aparece uma vez, na ordem
 * hierárquica, sem reduzir o que a faixa já exige. Validações ficam onde estão.
 */
export function mergeConditionSteps(
  steps: readonly MatrixStep[],
  added: readonly MatrixStep[],
): MatrixStep[] {
  const route = [...steps];
  for (const step of added) {
    if (route.some((s) => s.fn === step.fn && s.roleCode === step.roleCode)) continue;
    const rank = ORDER.get(step.fn) ?? 99;
    const index = route.findIndex((s) => (ORDER.get(s.fn) ?? 0) > rank);
    route.splice(index < 0 ? route.length : index, 0, step);
  }
  return route;
}

export interface RouteCenter {
  id: string;
  level: string | null;
  managerId: string | null;
  substituteId: string | null;
  inForce: boolean;
}

export interface RouteOffices {
  ceo: string | null;
  /** Substituto formal do CEO, só na matriz. O cadastro antigo de substituto não vale aqui. */
  ceoSubstitute?: string | null;
  backofficeHead: string | null;
  backofficeSubstitute: string | null;
}

export interface FunctionPerson {
  userId: string;
  substitute: boolean;
}

export interface FunctionTier {
  fn: RouteFunction;
  people: FunctionPerson[];
  centerId: string | null;
}

const centerPeople = (c: RouteCenter | undefined): FunctionPerson[] =>
  c?.inForce
    ? [
        ...(c.managerId ? [{ userId: c.managerId, substitute: false }] : []),
        ...(c.substituteId ? [{ userId: c.substituteId, substitute: true }] : []),
      ]
    : [];

/**
 * Quem responde pela função, em camadas: a própria função e as que a absorvem. Coordenador/Supervisor
 * sobe para Gestor, Gestor para Head/Diretor, Head/Diretor para Head de BackOffice. Falta de
 * responsável nunca sobe sozinha para o CEO. O CEO é uma camada só: o substituto formal entra apenas
 * com ausência formal registrada e vigente (`formalAbsent`), nunca por silêncio ou prazo vencido.
 *
 * Conflito: titular impedido perde a camada inteira. O substituto do centro pode ser subordinado de
 * quem pede, então ele só responde na ausência do titular, e a decisão sobe para a autoridade
 * seguinte. Head de BackOffice impedido encaminha ao CEO.
 */
export function functionTiers(
  fn: RouteFunction,
  chain: readonly RouteCenter[],
  offices: RouteOffices,
  impeded: ReadonlySet<string> = new Set(),
  formalAbsent: ReadonlySet<string> = new Set(),
): FunctionTier[] {
  const byLevel = (level: string) => chain.find((c) => c.inForce && c.level === level);
  const manager = byLevel('managerial');
  const master = byLevel('master');
  const tiers: Record<'cost_center_manager' | 'area_manager' | 'head_director', FunctionTier> = {
    cost_center_manager: {
      fn: 'cost_center_manager',
      people: centerPeople(chain[0]),
      centerId: chain[0]?.id ?? null,
    },
    area_manager: {
      fn: 'area_manager',
      people: centerPeople(manager),
      centerId: manager?.id ?? null,
    },
    head_director: {
      fn: 'head_director',
      people: centerPeople(master),
      centerId: master?.id ?? null,
    },
  };
  const backoffice: FunctionTier = {
    fn: 'backoffice_head',
    people: [
      ...(offices.backofficeHead ? [{ userId: offices.backofficeHead, substitute: false }] : []),
      ...(offices.backofficeSubstitute
        ? [{ userId: offices.backofficeSubstitute, substitute: true }]
        : []),
    ],
    centerId: null,
  };
  const ceo: FunctionTier = {
    fn: 'ceo',
    people: offices.ceo
      ? [
          { userId: offices.ceo, substitute: false },
          ...(offices.ceoSubstitute && formalAbsent.has(offices.ceo)
            ? [{ userId: offices.ceoSubstitute, substitute: true }]
            : []),
        ]
      : [],
    centerId: null,
  };
  const independent = (t: FunctionTier): FunctionTier =>
    t.people.some((p) => !p.substitute && impeded.has(p.userId)) ? { ...t, people: [] } : t;
  const withBackoffice = (list: FunctionTier[]) => [
    ...list.map(independent),
    independent(backoffice),
    ...(offices.backofficeHead && impeded.has(offices.backofficeHead) ? [ceo] : []),
  ];
  if (fn === 'cost_center_manager') {
    return withBackoffice([tiers.cost_center_manager, tiers.area_manager, tiers.head_director]);
  }
  if (fn === 'area_manager') return withBackoffice([tiers.area_manager, tiers.head_director]);
  if (fn === 'head_director') return withBackoffice([tiers.head_director]);
  if (fn === 'backoffice_head') return withBackoffice([]);
  if (fn === 'ceo') return [independent(ceo)];
  return [];
}

export interface SimulatedStep {
  fn: RouteFunction;
  purpose: StepPurpose;
  roleCode: string | null;
  /** Quem decide nesta etapa; nulo quando já foi cumprida ou está parada. */
  actorId: string | null;
  /** A pessoa que já cumpriu esta função ao decidir em etapa anterior. */
  satisfiedBy: string | null;
  /** Função prevista absorvida pela função que decide. */
  absorbedBy: RouteFunction | null;
  viaSubstitute: boolean;
  pending: boolean;
}

/**
 * Simula a rota inteira com a estrutura de hoje. `eligible` diz se a pessoa pode decidir agora, e
 * `impeded` lista quem não decide neste pedido. Perfis de validação vêm em `roleMembers`.
 */
export function simulateRoute(
  steps: readonly MatrixStep[],
  ctx: {
    chain: readonly RouteCenter[];
    offices: RouteOffices;
    impeded: ReadonlySet<string>;
    eligible: (userId: string) => boolean;
    roleMembers: (roleCode: string) => readonly string[];
    formalAbsent?: ReadonlySet<string>;
  },
): SimulatedStep[] {
  const decided = new Set<string>();
  const out: SimulatedStep[] = [];
  for (const step of steps) {
    const purpose = step.purpose ?? 'approval';
    const base = { fn: step.fn, purpose, roleCode: step.roleCode ?? null };
    const tiers: FunctionTier[] =
      step.fn === 'role'
        ? [
            {
              fn: 'role',
              people: ctx.roleMembers(step.roleCode ?? '').map((userId) => ({
                userId,
                substitute: false,
              })),
              centerId: null,
            },
          ]
        : functionTiers(step.fn, ctx.chain, ctx.offices, ctx.impeded, ctx.formalAbsent);
    const usable = (p: FunctionPerson) => ctx.eligible(p.userId) && !ctx.impeded.has(p.userId);
    const tier = tiers.find((t) => t.people.some(usable));
    const people = tier?.people.filter(usable) ?? [];
    const absorbedBy = tier && tier.fn !== step.fn ? tier.fn : null;
    // Substituto só responde pela função na falta do titular: não cumpre a etapa no lugar dele.
    const holders = people.filter((p) => !p.substitute);
    const answering = holders.length > 0 ? holders : people;
    const done = answering.find((p) => purpose === 'approval' && decided.has(p.userId));
    if (done) {
      out.push({
        ...base,
        actorId: null,
        satisfiedBy: done.userId,
        absorbedBy,
        viaSubstitute: false,
        pending: false,
      });
      continue;
    }
    const actor = people[0];
    if (!actor) {
      out.push({
        ...base,
        actorId: null,
        satisfiedBy: null,
        absorbedBy: null,
        viaSubstitute: false,
        pending: true,
      });
      continue;
    }
    if (purpose === 'approval') decided.add(actor.userId);
    out.push({
      ...base,
      actorId: actor.userId,
      satisfiedBy: null,
      absorbedBy,
      viaSubstitute: actor.substitute,
      pending: false,
    });
  }
  return out;
}

// ----- Entradas -----

const matrixStepInput = z
  .object({
    fn: z.enum(ROUTE_FUNCTIONS),
    purpose: z.enum(STEP_PURPOSES).optional(),
    roleCode: z.string().trim().min(1).max(60).optional(),
  })
  .refine((s) => (s.fn === 'role') === !!s.roleCode, {
    message: 'Validação por perfil precisa do perfil de acesso',
    path: ['roleCode'],
  });

const matrixBandInput = z.object({
  code: z.string().trim().min(1).max(20),
  minAmount: decimalString(15, 4).nullable(),
  maxAmount: decimalString(15, 4).nullable(),
  steps: z.array(matrixStepInput).max(10),
});

/** Faixas do rascunho de uma tabela: nada disso vale para aprovações reais. */
export const matrixDraftInput = z
  .object({ bands: z.array(matrixBandInput).min(1).max(20) })
  .superRefine((v, ctx) => {
    for (const message of matrixBandIssues(v.bands)) {
      ctx.addIssue({ code: 'custom', message, path: ['bands'] });
    }
  });
export type MatrixDraftInput = z.infer<typeof matrixDraftInput>;

/** Regra de um processo: valores próprios, quem decide (sem valor) ou não aplicar a matriz. */
export const matrixProcessInput = z
  .object({
    applies: z.boolean(),
    limits: z.array(decimalString(15, 4)).length(3).optional(),
    steps: z.array(matrixStepInput).min(1).max(10).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.applies && !v.limits === !v.steps) {
      ctx.addIssue({
        code: 'custom',
        message: 'Informe os valores ou quem decide',
        path: ['limits'],
      });
    }
    const [a1, a2, a3] = (v.limits ?? []).map(toScaled);
    if (a1 != null && !(a1 < a2! && a2! < a3!)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Cada faixa precisa ser maior que a anterior',
        path: ['limits'],
      });
    }
  });
export type MatrixProcessInput = z.infer<typeof matrixProcessInput>;

export const matrixConditionInput = z.object({
  adds: z.array(matrixStepInput).max(5),
  isActive: z.boolean(),
});

export const matrixSimulationInput = z
  .object({
    process: z.string().trim().min(1).max(40),
    legalEntityId: uuid,
    costCenterId: uuid,
    amount: decimalString(15, 4).nullable().optional(),
    currency: currencyCode.nullable().optional(),
    conditions: z.array(z.string().trim().min(1).max(40)).max(10).default([]),
    requesterUserId: uuid.nullable().optional(),
  })
  .refine((v) => !v.amount || !!v.currency, {
    message: 'Informe a moeda',
    path: ['currency'],
  });
export type MatrixSimulationInput = z.infer<typeof matrixSimulationInput>;
